import { useMemo, useState } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { timelineItems, type TimelineStage } from "@/mocks/digData";

const filters: Array<{ key: "全部" | TimelineStage; label: string }> = [
  { key: "全部", label: "全部更新" },
  { key: "反转", label: "后续反转" },
  { key: "发声", label: "当事人发声" },
  { key: "回应", label: "官方回应" },
  { key: "进展", label: "最新进展" },
];

const stageStyle: Record<TimelineStage, string> = {
  反转: "bg-primary-100 text-primary-800 border-primary-200",
  发声: "bg-secondary-100 text-secondary-900 border-secondary-200",
  回应: "bg-accent-100 text-accent-900 border-accent-200",
  进展: "bg-background-200 text-foreground-800 border-background-300",
};

export default function Timeline() {
  const [active, setActive] = useState<"全部" | TimelineStage>("全部");

  const visibleItems = useMemo(
    () => (active === "全部" ? timelineItems : timelineItems.filter((item) => item.stage === active)),
    [active],
  );

  return (
    <section id="timeline" className="scroll-mt-24 py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="LIVE TIMELINE"
            title="最新进展 · 按时间倒序追更"
            desc="每条更新都标注阶段、来源与热度，你可以按「反转 / 发声 / 回应 / 进展」快速筛选，只看你关心的那一类。"
          />
          <div className="flex flex-wrap gap-2 lg:justify-end">
            {filters.map((filter) => {
              const isActive = active === filter.key;
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setActive(filter.key)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary-500 border-primary-500 text-background-50"
                      : "bg-background-100 border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        <ol className="mt-10 md:mt-14 relative border-l border-background-300 pl-6 md:pl-10 space-y-6 md:space-y-8">
          {visibleItems.map((item) => (
            <li key={item.id} className="relative">
              <span className="absolute -left-[31px] md:-left-[47px] top-2 w-3 h-3 rounded-full bg-primary-500 ring-4 ring-background-50" />
              <article className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-6 hover:border-primary-300 transition-colors">
                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs border ${stageStyle[item.stage]}`}
                  >
                    {item.stage}
                  </span>
                  <time className="text-xs md:text-sm text-foreground-500">
                    {item.date} {item.clock}
                  </time>
                  <span className="text-xs md:text-sm text-foreground-500">
                    · 所属：{item.seriesName}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs md:text-sm text-primary-600">
                    <i className="ri-fire-line" />
                    {item.heat}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-lg md:text-xl font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {item.desc}
                </p>
                <div className="mt-3 pt-3 border-t border-background-200 flex items-center gap-2 text-xs md:text-sm text-foreground-500">
                  <i className="ri-links-line" />
                  信息来源：{item.source}
                </div>
              </article>
            </li>
          ))}
        </ol>

        {visibleItems.length === 0 && (
          <p className="mt-10 text-center text-foreground-500">该分类下暂无更新，换个标签看看。</p>
        )}
      </div>
    </section>
  );
}